import { toastError } from 'admination-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  GEVA_KLETT_DOKUMENT,
  getGevaKlettStatus,
  type GevaKlettStatus,
} from '../../api/gevaKlett'
import type { TabOverrides } from '@/core/components/modal/ModalHeader'
import { gevaKlettStatusFailureMessage } from './errors'

export type GevaKlettStep = 'klett' | 'geva'

const EMPTY_STATUS: GevaKlettStatus = {
  klett: { fertig: false, niveau: null, punkte: null, punkte_max: null, bereiche: null, dokument_id: null },
  geva: { fertig: false, merkmale: null, dossier: null, dokument_id: null },
}

export function gevaKlettTabs(status: GevaKlettStatus): TabOverrides {
  return {
    ...(status.klett.fertig && { klett: { label: GEVA_KLETT_DOKUMENT.klett, icon: 'i-doc', completed: true } }),
    ...(status.geva.fertig && { geva: { label: GEVA_KLETT_DOKUMENT.geva, icon: 'i-doc', completed: true } }),
  }
}

export function useStatusFehlerToast(fehler: string | null): void {
  useEffect(() => {
    if (fehler) toastError(fehler)
  }, [fehler])
}

export type GevaKlettStatusState = ReturnType<typeof useGevaKlettStatus>

export function useGevaKlettStatus(coachingId: string | null) {
  const { t } = useTranslation('vergabeStaff')
  const [status, setStatus] = useState(EMPTY_STATUS)
  const [loading, setLoading] = useState(true)
  const [statusFehler, setStatusFehler] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setStatus(EMPTY_STATUS)
    setStatusFehler(null)
    setLoading(!!coachingId)
    if (!coachingId) return
    getGevaKlettStatus(coachingId)
      .then((status) => {
        if (cancelled) return
        setStatus(status)
      })
      .catch((e) => {
        console.error('GEVA/Klett-Status laden fehlgeschlagen:', e)
        if (cancelled) return
        setStatusFehler(gevaKlettStatusFailureMessage(e, t))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // `t` is stable for a namespace; the read is once per coaching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachingId])

  const markDone = <S extends GevaKlettStep>(step: S, value: GevaKlettStatus[S]) => {
    setStatus((prev) => ({ ...prev, [step]: value }))
  }

  return { status, loading, statusError: statusFehler !== null, statusFehler, markDone }
}
